import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, FileText } from "lucide-react";
import api, { fmtErr } from "../../lib/api";
import { refreshSiteContent } from "../../hooks/useSiteContent";

const inputCls = "w-full bg-white border border-black/15 px-3 py-2 text-sm font-light focus:outline-none focus:border-black";
const textareaCls = `${inputCls} resize-none`;

const Field = ({ label, value, onChange, textarea = false, rows = 3, placeholder = "" }) => (
  <label className="flex flex-col gap-1.5 text-xs font-light text-black/60">
    {label}
    {textarea ? (
      <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={textareaCls} />
    ) : (
      <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputCls} />
    )}
  </label>
);

const Section = ({ title, children }) => (
  <div className="bg-white border border-black/10 p-6 space-y-4">
    <h3 className="font-display text-xl tracking-tight flex items-center gap-2">
      <FileText size={16} className="text-clay" /> {title}
    </h3>
    <div className="space-y-4">{children}</div>
  </div>
);

export const SiteContentAdmin = () => {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/site-content").then((res) => setDraft(res.data || {})).catch(() => setDraft({}));
  }, []);

  if (!draft) return <p className="label-editorial text-black/40">A carregar…</p>;

  const setSec = (section, patch) => setDraft((d) => ({ ...d, [section]: { ...(d[section] || {}), ...patch } }));
  const setFeature = (idx, val) => {
    const list = [...(draft.espaco?.features || ["", "", "", ""])];
    list[idx] = val;
    setSec("espaco", { features: list });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-content", draft);
      refreshSiteContent();
      toast.success("Conteúdos guardados. O site foi atualizado.");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const hero = draft.hero || {};
  const pos = draft.positioning || {};
  const story = draft.story || {};
  const espaco = draft.espaco || {};
  const contact = draft.contact || {};
  const features = espaco.features || ["", "", "", ""];

  return (
    <div data-testid="site-content-panel" className="space-y-6">
      <p className="text-black/50 font-light max-w-3xl">
        Edite os textos principais do site público. Cada título grande está
        dividido em três partes: <strong>antes</strong>, <strong>palavra em destaque</strong> (aparece em itálico cor de marca) e <strong>depois</strong>.
        Deixe qualquer parte vazia se não quiser usar.
      </p>

      <Section title="Hero (início da página)">
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Kicker" value={hero.kicker} onChange={(v) => setSec("hero", { kicker: v })} placeholder="Clínica de Imagem · Leiria" />
          <Field label="CTA principal" value={hero.cta_primary} onChange={(v) => setSec("hero", { cta_primary: v })} placeholder="Agendar Marcação" />
          <Field label="CTA secundário" value={hero.cta_secondary} onChange={(v) => setSec("hero", { cta_secondary: v })} placeholder="Conhecer Serviços" />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Título — antes" value={hero.title_before} onChange={(v) => setSec("hero", { title_before: v })} placeholder="A sua imagem," />
          <Field label="Título — destaque (itálico)" value={hero.title_highlight} onChange={(v) => setSec("hero", { title_highlight: v })} placeholder="elevada" />
          <Field label="Título — depois" value={hero.title_after} onChange={(v) => setSec("hero", { title_after: v })} placeholder="ao detalhe." />
        </div>
        <Field label="Subtítulo" value={hero.subtitle} onChange={(v) => setSec("hero", { subtitle: v })} textarea rows={3} />
      </Section>

      <Section title="Quem Somos">
        <Field label="Kicker" value={pos.kicker} onChange={(v) => setSec("positioning", { kicker: v })} placeholder="Quem somos" />
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Título — antes" value={pos.heading_before} onChange={(v) => setSec("positioning", { heading_before: v })} textarea rows={2} />
          <Field label="Título — destaque (itálico)" value={pos.heading_highlight} onChange={(v) => setSec("positioning", { heading_highlight: v })} />
          <Field label="Título — depois" value={pos.heading_after} onChange={(v) => setSec("positioning", { heading_after: v })} textarea rows={2} />
        </div>
        <Field label="Parágrafo" value={pos.paragraph} onChange={(v) => setSec("positioning", { paragraph: v })} textarea rows={4} />
      </Section>

      <Section title="A Nossa Essência (Story)">
        <Field label="Kicker" value={story.kicker} onChange={(v) => setSec("story", { kicker: v })} />
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Título — antes" value={story.heading_before} onChange={(v) => setSec("story", { heading_before: v })} />
          <Field label="Título — destaque (itálico)" value={story.heading_highlight} onChange={(v) => setSec("story", { heading_highlight: v })} />
          <Field label="Título — depois" value={story.heading_after} onChange={(v) => setSec("story", { heading_after: v })} />
        </div>
        <Field label="Parágrafo 1" value={story.paragraph1} onChange={(v) => setSec("story", { paragraph1: v })} textarea rows={3} />
        <Field label="Parágrafo 2" value={story.paragraph2} onChange={(v) => setSec("story", { paragraph2: v })} textarea rows={3} />
      </Section>

      <Section title="O Espaço">
        <Field label="Kicker" value={espaco.kicker} onChange={(v) => setSec("espaco", { kicker: v })} />
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Título home — antes" value={espaco.home_heading_before} onChange={(v) => setSec("espaco", { home_heading_before: v })} />
          <Field label="Título home — destaque" value={espaco.home_heading_highlight} onChange={(v) => setSec("espaco", { home_heading_highlight: v })} />
          <Field label="Título home — depois" value={espaco.home_heading_after} onChange={(v) => setSec("espaco", { home_heading_after: v })} />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Título /espaco — antes" value={espaco.page_heading_before} onChange={(v) => setSec("espaco", { page_heading_before: v })} />
          <Field label="Título /espaco — destaque" value={espaco.page_heading_highlight} onChange={(v) => setSec("espaco", { page_heading_highlight: v })} />
          <Field label="Título /espaco — depois" value={espaco.page_heading_after} onChange={(v) => setSec("espaco", { page_heading_after: v })} />
        </div>
        <Field label="Intro" value={espaco.intro} onChange={(v) => setSec("espaco", { intro: v })} textarea rows={4} />
        <div>
          <p className="text-xs text-black/60 mb-2">Características (4 itens)</p>
          <div className="grid md:grid-cols-2 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <input key={i} value={features[i] || ""} onChange={(e) => setFeature(i, e.target.value)} placeholder={`Item ${i + 1}`} className={inputCls} />
            ))}
          </div>
        </div>
      </Section>

      <Section title="Contactos">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Morada" value={contact.address} onChange={(v) => setSec("contact", { address: v })} />
          <Field label="Cidade" value={contact.city} onChange={(v) => setSec("contact", { city: v })} />
          <Field label="Telemóvel" value={contact.mobile} onChange={(v) => setSec("contact", { mobile: v })} />
          <Field label="Telefone" value={contact.phone} onChange={(v) => setSec("contact", { phone: v })} />
          <Field label="Email" value={contact.email} onChange={(v) => setSec("contact", { email: v })} />
          <Field label="Horários" value={contact.hours} onChange={(v) => setSec("contact", { hours: v })} />
          <Field label="Facebook (URL)" value={contact.facebook} onChange={(v) => setSec("contact", { facebook: v })} />
          <Field label="Instagram (URL)" value={contact.instagram} onChange={(v) => setSec("contact", { instagram: v })} />
          <Field label="WhatsApp (URL)" value={contact.whatsapp} onChange={(v) => setSec("contact", { whatsapp: v })} />
          <Field label="YouTube (URL)" value={contact.youtube} onChange={(v) => setSec("contact", { youtube: v })} placeholder="https://www.youtube.com/@innpact" />
        </div>
      </Section>

      <div className="sticky bottom-4 z-10 flex justify-end">
        <button type="button" onClick={save} disabled={saving} data-testid="site-content-save" className="flex items-center gap-2 label-editorial bg-black text-white px-8 py-3 hover:bg-black/80 transition-colors disabled:opacity-50 shadow-lg">
          <Save size={15} /> {saving ? "A guardar…" : "Guardar Alterações"}
        </button>
      </div>
    </div>
  );
};

export default SiteContentAdmin;
